import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const COMPLAINT_TYPES = [
  { value: 'billing', label: 'Billing Issue' },
  { value: 'technical', label: 'Technical Problem' },
  { value: 'content', label: 'Content Concern' },
  { value: 'service_quality', label: 'Service Quality' },
  { value: 'privacy', label: 'Privacy Concern' },
  { value: 'discrimination', label: 'Discrimination/Harassment' },
  { value: 'data_accuracy', label: 'Data Accuracy' },
  { value: 'cancellation', label: 'Cancellation Issue' },
  { value: 'other', label: 'Other' }
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low - Minor inconvenience' },
  { value: 'medium', label: 'Medium - Affecting my experience' },
  { value: 'high', label: 'High - Significant problem' },
  { value: 'critical', label: 'Critical - Urgent issue' }
];

export function UserComplaintForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [complaintType, setComplaintType] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [description, setDescription] = useState("");

  const handleSubmit = async () => {
    if (!complaintType || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('You must be logged in to submit a complaint');
        return;
      }

      const { data, error } = await supabase
        .from('user_complaints')
        .insert({
          user_id: user.id,
          complaint_type: complaintType,
          severity,
          description,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // Send email notification
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('user_id', user?.id || '')
          .single();

        await supabase.functions.invoke('send-business-notification', {
          body: {
            type: 'complaint',
            complaintId: data?.id || '',
            userId: user?.id || '',
            userEmail: user?.email || '',
            userName: profile?.display_name || 'User',
            complaintType: complaintType,
            severity,
            description: description.trim(),
          },
        });
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't fail the submission if email fails
      }

      toast.success('Your complaint has been submitted. You\'ll receive an email confirmation, and our team will review this within 5-7 working days.');
      
      // Reset form
      setComplaintType("");
      setSeverity("medium");
      setDescription("");
      setIsOpen(false);
    } catch (error) {
      console.error('Error submitting complaint:', error);
      toast.error('Failed to submit complaint. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <MessageSquare className="h-4 w-4" />
          Submit Complaint
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Submit a Complaint</DialogTitle>
          <DialogDescription>
            We take all feedback seriously. Please provide details about your concern.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="complaint-type">Complaint Type *</Label>
            <Select value={complaintType} onValueChange={setComplaintType}>
              <SelectTrigger id="complaint-type">
                <SelectValue placeholder="Select complaint type" />
              </SelectTrigger>
              <SelectContent>
                {COMPLAINT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="severity">Severity Level *</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITY_LEVELS.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    {level.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide as much detail as possible about your complaint..."
              rows={8}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Include dates, times, and any relevant details to help us investigate
            </p>
          </div>

          <div className="bg-muted/50 p-4 rounded-md text-sm">
            <p className="font-medium mb-2">What happens next?</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>You'll receive an email confirmation immediately</li>
              <li>Your complaint will be reviewed by our team within 24-48 hours</li>
              <li>We'll investigate the issue thoroughly</li>
              <li>You'll receive a response via email with our findings and resolution</li>
              <li>For urgent safety concerns about AI behaviour, use the Report Safety Concern option instead</li>
            </ul>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="gap-2">
              <Send className="h-4 w-4" />
              {isSubmitting ? 'Submitting...' : 'Submit Complaint'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
